import { useEffect, useState } from 'react';
import { Loader2, Check, X, ClipboardList, PauseCircle, UserX } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    useClassRegisterQuery,
    useMarkClassRegisterMutation,
} from '../../services/hooks/useEventos';
import type { AttendanceStatus, RegisterAttendee } from '../../services/api/eventoService';
import PeopleTableHeader from './PeopleTableHeader';

/** La marca, en la columna del medio: la misma rejilla que las otras tablas. */
const ESTADO: Record<'present' | 'absent', { label: string; className: string }> = {
    present: { label: 'Vino', className: 'text-green-700' },
    absent: { label: 'No vino', className: 'text-red-600' },
};

interface ClassRegisterProps {
    eventoId?: string;
    /**
     * P6 fase 2 — pedirla por la puerta del instructor (`/teaching`), que valida
     * que la clase es suya. Mismo panel; solo cambia de dónde salen los datos.
     */
    teaching?: boolean;
}

/**
 * P6 — the register for one class, inside the event detail.
 *
 * Three states, and the third one is the point: `status === null` means nobody
 * has marked that person. It is never rendered as "absent", because an absence
 * consumes the client's class and a forgotten register must not do that.
 */
const ClassRegister = ({ eventoId, teaching = false }: ClassRegisterProps) => {
    const { data, isLoading } = useClassRegisterQuery(eventoId, true, teaching);
    const { mutateAsync: guardar, isPending } = useMarkClassRegisterMutation(eventoId, teaching);

    // Local copy so the operator can tick several people and save once. Seeded
    // from the server and re-seeded whenever the server copy changes.
    const [draft, setDraft] = useState<Record<string, AttendanceStatus>>({});

    useEffect(() => {
        if (!data) return;
        const seeded: Record<string, AttendanceStatus> = {};
        for (const a of data.attendees) {
            if (a.status) seeded[`${a.kind}:${a.id}`] = a.status;
        }
        setDraft(seeded);
    }, [data]);

    if (!eventoId) return null;

    if (isLoading) {
        return (
            <div className="mt-6 flex justify-center">
                <Loader2 className="animate-spin" />
            </div>
        );
    }

    if (!data) return null;

    // A cancelled class had no attendees, so there is nothing to take.
    if (data.cancelled) return null;

    const key = (a: RegisterAttendee) => `${a.kind}:${a.id}`;
    const editable = data.window.state !== 'early';

    const set = (a: RegisterAttendee, status: AttendanceStatus) =>
        setDraft((prev) => ({ ...prev, [key(a)]: status }));

    const markAll = (status: AttendanceStatus) =>
        setDraft(Object.fromEntries(data.attendees.map((a) => [key(a), status])));

    const dirty = data.attendees.some((a) => (draft[key(a)] ?? null) !== a.status);
    const sinMarcar = data.attendees.filter((a) => !draft[key(a)]).length;

    const onSave = async () => {
        const marks = data.attendees
            .filter((a) => draft[key(a)] && draft[key(a)] !== a.status)
            .map((a) => ({ kind: a.kind, id: a.id, status: draft[key(a)] }));
        if (marks.length) await guardar(marks);
    };

    return (
        <section>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div className="flex items-center gap-2">
                    <h3 className="font-bold text-sm uppercase tracking-wide text-gray-600 flex items-center gap-2">
                        <ClipboardList size={14} />
                        Asistencia ({data.attendees.length})
                    </h3>
                    {!data.taken && (
                        <span className="text-xs rounded-full bg-gray-100 text-gray-600 px-2 py-0.5">
                            Sin pasar
                        </span>
                    )}
                </div>

                {editable && data.attendees.length > 0 && (
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => markAll('present')}
                            disabled={isPending}
                            className="text-xs px-3 py-1.5 rounded-md bg-brand-primary text-white hover:brightness-90 disabled:opacity-50"
                        >
                            Todos vinieron
                        </button>
                        <button
                            type="button"
                            onClick={onSave}
                            disabled={isPending || !dirty}
                            className="text-xs px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 flex items-center gap-1"
                        >
                            {isPending && <Loader2 className="animate-spin" size={12} />}
                            Guardar
                        </button>
                    </div>
                )}
            </div>

            {/* Por qué no se puede marcar todavía. Sin esto, los interruptores
                deshabilitados no se explican solos. */}
            {data.window.state === 'early' && (
                <p className="text-xs text-gray-500 mt-2">
                    La lista se abre cuando empieza la clase
                    {' '}({format(new Date(data.window.startsAt), "d MMM 'a las' HH:mm", { locale: es })}).
                </p>
            )}
            {data.window.state === 'closed' && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2">
                    La clase ya pasó: solo un administrador puede cambiar la lista.
                </p>
            )}

            {data.attendees.length === 0 ? (
                <p className="text-sm text-gray-500">No hay nadie apuntado a esta clase.</p>
            ) : (
                <div className="bg-white rounded-lg border border-gray-200">
                    <PeopleTableHeader middle="Estado" />
                    <ul className="divide-y divide-gray-100">
                        {data.attendees.map((a) => {
                            const status = draft[key(a)] ?? null;
                            return (
                                <li
                                    key={key(a)}
                                    className="grid grid-cols-12 gap-2 items-center px-3 py-2"
                                >
                                    <div className="col-span-12 lg:col-span-5 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">{a.name}</p>
                                        {a.kind === 'prospect' && (
                                            <span className="mt-0.5 inline-flex items-center rounded-full bg-purple-50 px-2 py-0.5 text-[11px] font-semibold text-purple-800">
                                                Clase de prueba
                                            </span>
                                        )}
                                        {/* En pausa sobre ESTA clase. Al pausar se le saca
                                            de sus clases futuras, así que esto queda para
                                            las que ya habían empezado y para quien vuelva
                                            a entrar por la asignación masiva, que no mira
                                            `enEspera`: marcarle ausente le quemaría una
                                            clase que no le tocaba. */}
                                        {a.paused && (
                                            <span
                                                className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
                                                title={['Su plaza está en pausa en esta fecha', a.motivo]
                                                    .filter(Boolean).join(' · ')}
                                            >
                                                <PauseCircle size={11} /> En pausa
                                            </span>
                                        )}
                                    </div>

                                    <div className="col-span-12 lg:col-span-5 min-w-0 text-xs">
                                        {status === null
                                            ? <span className="text-gray-400">sin marcar</span>
                                            : <span className={ESTADO[status].className}>{ESTADO[status].label}</span>}
                                    </div>

                                    <div className="col-span-12 lg:col-span-2 flex lg:justify-end items-center gap-1">
                                        <button
                                            type="button"
                                            onClick={() => set(a, 'present')}
                                            disabled={!editable || isPending}
                                            aria-label={`Marcar a ${a.name} como presente`}
                                            className={`p-1.5 rounded-md border disabled:opacity-40 ${
                                                status === 'present'
                                                    ? 'bg-green-600 border-green-600 text-white'
                                                    : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                                            }`}
                                        >
                                            <Check size={14} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => set(a, 'absent')}
                                            disabled={!editable || isPending}
                                            aria-label={`Marcar a ${a.name} como ausente`}
                                            className={`p-1.5 rounded-md border disabled:opacity-40 ${
                                                status === 'absent'
                                                    ? 'bg-red-500 border-red-500 text-white'
                                                    : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                                            }`}
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {sinMarcar > 0 && data.attendees.length > 0 && editable && (
                <p className="text-xs text-gray-500 mt-2">
                    Quedan {sinMarcar} sin marcar. Lo que no se marca no cuenta como ausencia.
                </p>
            )}

            {/* Quien tenía plaza y canceló. En lectura: no se le marca nada, no se
                le esperaba. Sale para que no parezca que falta sin avisar. */}
            {data.cancellations.length > 0 && (
                <div className="mt-4">
                    <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                        <UserX size={12} /> Canceló esta clase
                    </p>
                    <ul className="mt-1 space-y-0.5">
                        {data.cancellations.map((c) => (
                            <li key={c.clientId} className="text-sm text-gray-600">
                                {c.name}
                                {c.recoveryGranted === false && ' — sin recuperación'}
                                {c.recoveryGranted === true && ' — con recuperación'}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
};

export default ClassRegister;
